import { CURRENT_CLIENT } from '@/constants'

export const EMPLOYER_ORGANISATION_SETTINGS_HUB_PATH = '/employer/organisation/settings'

export const EMPLOYER_ORGANISATION_SETTINGS_GENERAL_SEGMENT = 'general'
export const EMPLOYER_ORGANISATION_SETTINGS_TEAM_SEGMENT = 'team'
export const EMPLOYER_ORGANISATION_SETTINGS_PREFERENCES_SEGMENT = 'preferences'
export const EMPLOYER_ORGANISATION_SETTINGS_BILLING_SEGMENT = 'billing'

const EMPLOYER_ORGANISATION_SETTINGS_SLUG_RE =
  /^\/employer\/organisation\/[^/]+\/settings(?:\/|$)/

const EMPLOYER_ORGANISATION_SETTINGS_HUB_RE =
  /^\/employer\/organisation\/settings(?:\/|$)/

export type EmployerOrganisationSettingsSection = {
  segment: string
  label: string
  icon: string
  description: string
}

const EMPLOYER_ORGANISATION_SETTINGS_BASE_SECTIONS: EmployerOrganisationSettingsSection[] = [
  {
    segment: EMPLOYER_ORGANISATION_SETTINGS_GENERAL_SEGMENT,
    label: 'General',
    icon: 'Settings',
    description: "Manage this organisation's name, logo, URL slug, and workspace.",
  },
  {
    segment: EMPLOYER_ORGANISATION_SETTINGS_TEAM_SEGMENT,
    label: 'Team',
    icon: 'Users',
    description: 'Invite teammates and manage who has access to this organisation.',
  },
  {
    segment: EMPLOYER_ORGANISATION_SETTINGS_PREFERENCES_SEGMENT,
    label: 'Preferences',
    icon: 'SlidersHorizontal',
    description: 'Defaults for new job postings and application alerts.',
  },
]

function billingSettingsSection(): EmployerOrganisationSettingsSection | null {
  const raw = (CURRENT_CLIENT as { features?: { upgradePlan?: { enabled?: boolean; label?: string; externalUrl?: string | null } } })
    .features?.upgradePlan
  if (raw?.enabled !== true || raw.externalUrl?.trim()) return null
  return {
    segment: EMPLOYER_ORGANISATION_SETTINGS_BILLING_SEGMENT,
    label: raw.label?.trim() || 'Plan & Billing',
    icon: 'CreditCard',
    description: 'View your subscription, upgrade your plan, and manage billing for this organisation.',
  }
}

export function getEmployerOrganisationSettingsSections(): EmployerOrganisationSettingsSection[] {
  const billing = billingSettingsSection()
  if (!billing) return EMPLOYER_ORGANISATION_SETTINGS_BASE_SECTIONS
  return [...EMPLOYER_ORGANISATION_SETTINGS_BASE_SECTIONS, billing]
}

export function getEmployerOrganisationSettingsSection(
  segment: string,
): EmployerOrganisationSettingsSection | undefined {
  return getEmployerOrganisationSettingsSections().find((section) => section.segment === segment)
}

export type EmployerOrganisationSettingsNavItem = {
  path: string
  label: string
  icon: string
  description?: string
}

function settingsBasePath(slug?: string | null): string {
  const trimmed = slug?.trim()
  if (trimmed) {
    return `/employer/organisation/${encodeURIComponent(trimmed)}/settings`
  }
  return EMPLOYER_ORGANISATION_SETTINGS_HUB_PATH
}

export function employerOrganisationSettingsPath(slug: string, segment?: string): string {
  const base = settingsBasePath(slug)
  if (!segment) return base
  return `${base}/${segment}`
}

export function employerOrganisationSettingsHubPath(segment?: string): string {
  if (!segment) return EMPLOYER_ORGANISATION_SETTINGS_HUB_PATH
  return `${EMPLOYER_ORGANISATION_SETTINGS_HUB_PATH}/${segment}`
}

/** Slug from `/employer/organisation/:slug/settings…` when not on the hub path. */
export function employerOrganisationSettingsSlugFromPath(pathname: string): string | null {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  const match = normalized.match(/^\/employer\/organisation\/([^/]+)\/settings(?:\/|$)/)
  if (!match?.[1] || match[1] === 'settings') return null
  return decodeURIComponent(match[1])
}

export function getEmployerOrganisationSettingsNavItems(opts?: {
  slug?: string | null
}): EmployerOrganisationSettingsNavItem[] {
  const base = settingsBasePath(opts?.slug)
  return getEmployerOrganisationSettingsSections().map((section) => ({
    path: `${base}/${section.segment}`,
    label: section.label,
    icon: section.icon,
    description: section.description,
  }))
}

export function isEmployerOrganisationSettingsPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  return (
    EMPLOYER_ORGANISATION_SETTINGS_HUB_RE.test(normalized) ||
    EMPLOYER_ORGANISATION_SETTINGS_SLUG_RE.test(normalized)
  )
}
